import React, { useState } from 'react';
import { 
    AppBar, 
    Toolbar, 
    Typography, 
    IconButton, 
    Box,
    Divider,
    Tooltip
} from '@mui/material';
import { Home, Menu as MenuIcon, Person, Logout, Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getUser, getPostLoginPath, logout } from '../../services/auth';
import { formatDisplayDateTime } from '../../utils/dateUtils';
import UserPreferencesDialog from '../UserPreferencesDialog/UserPreferencesDialog';
import { styled } from '@mui/material/styles';

const Logo = styled('img')({
    height: '40px',
    marginRight: '12px',
});

interface TitleBarProps {
    title?: string;
}

const TitleBar: React.FC<TitleBarProps> = ({ title }) => {
    const navigate = useNavigate();
    const user = getUser();
    const [preferencesOpen, setPreferencesOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <AppBar 
            position="static" 
            sx={{ 
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                boxShadow: '0 4px 20px 0 rgba(37, 99, 235, 0.15)'
            }}
        >
            <Toolbar sx={{ py: 1 }}>
                <Logo src="/logo.png" alt="Statusense Logo" />
                
                <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                        flexGrow: 1,
                        fontWeight: 600,
                        letterSpacing: '0.5px'
                    }}
                >
                    {title}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Your start page (from preferences)">
                        <IconButton
                            color="inherit"
                            onClick={() => navigate(getPostLoginPath())}
                            aria-label="Go to your start page"
                        >
                            <Home />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Main Menu">
                        <IconButton
                            color="inherit"
                            onClick={() => navigate('/home')}
                            aria-label="Open main menu"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="My settings">
                        <IconButton
                            color="inherit"
                            onClick={() => setPreferencesOpen(true)}
                            aria-label="My settings"
                        >
                            <Settings />
                        </IconButton>
                    </Tooltip>
                    
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)', mx: 1 }} />
                    
                    <Tooltip 
                        title={
                            <Box sx={{ p: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Id:</strong> {user?.user_name || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Name:</strong> {user?.display_name || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Email:</strong> {user?.email || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Client:</strong> {user?.client_id || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Status:</strong> {user?.status || 'N/A'}
                                </Typography>                                
                                {user?.attributes && Object.keys(user.attributes).length > 0 ? (
                                    Object.entries(user.attributes).map(([key, value]) => (
                                        <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>{key}:</strong> {String(value) || 'N/A'}
                                        </Typography>
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Previous Login:</strong> {user?.last_login_at ? formatDisplayDateTime(user.last_login_at) : 'N/A'}
                                    </Typography>
                                )}
                                <Typography variant="body2">
                                    <strong>Roles:</strong> {user?.roles.map(role => role.role_name).join(', ') || 'No roles'}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="bottom-end"
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: 2,
                            px: 2.5,
                            py: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)'
                            }
                        }}>
                            <Person sx={{ mr: 1, fontSize: '1.1rem' }} />
                            <Box>
                                <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                    {user?.display_name || user?.user_name}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                                    {user?.roles.map(role => role.role_name).join(', ')}
                                </Typography>
                            </Box>
                        </Box>
                    </Tooltip>
                    
                    <IconButton 
                        color="inherit" 
                        onClick={handleLogout}
                        sx={{ ml: 1 }}
                        aria-label="Log out"
                    >
                        <Logout />
                    </IconButton>
                </Box>
            </Toolbar>
            <UserPreferencesDialog open={preferencesOpen} onClose={() => setPreferencesOpen(false)} />
        </AppBar>
    );
};

export default TitleBar;
